<template>
  <b-col tag="section" cols="12" :class="{ 'text-gray' : disabled }">
    <slot name="header" />

    <slot name="top-content" />

    <ErrorSearch ajax
                 class="mb-4 py-4"
                 :disabled="disabled"
                 :action="config.errorInputUrl"
                 is-troubleshooting-search
    />

    <overlay-wrapper v-if="!disabled && !error.present" :show="loading">
      <b-row>
        <b-col cols="12" class="d-none d-md-flex">
          <b-table v-if="items.loaded.length"
                   borderless
                   small
                   hover
                   head-variant="dark"
                   class="troubleshooting_table"
                   thead-tr-class="small"
                   :items="items.loaded"
                   :fields="fields"
                   @row-clicked="sessionDetails"
          >
            <template v-slot:cell(title)="data">
              <span class="font-condensed">{{ data.value }}</span>
            </template>

            <template v-slot:cell(view)="data">
              <b-link class="font-condensed text-nowrap" @click="sessionDetails(data.item)">
                {{ 'faultDetails' | translate(labels, 'TroubleshootingProcedures') }}
                <b-icon icon="caret-right" class="ml-1" />
              </b-link>
            </template>

            <template v-slot:cell(score)="data">
              <b-progress v-b-tooltip="{ title: 'Rank: ' + data.value }"
                          height=".5rem"
                          variant="success"
                          style="min-width: 3.5rem"
                          class="mr-2"
                          :value="data.value * 100"
              />
            </template>

            <template v-slot:cell(tree_id)="data">
              <b-btn size="sm"
                     variant="primary"
                     class="font-condensed text-nowrap w-100"
                     :disabled="session.starting === activeItem.tree_id || data.item.sessionError"
                     :href="data.value"
                     @click.prevent="startSession(data.item)"
              >
                <template v-if="session.starting === data.item.tree_id">
                  <b-spinner variant="light" small />
                </template>
                {{ 'startSession' | translate(labels, 'TroubleshootingProcedures') }}
              </b-btn>
            </template>
          </b-table>
        </b-col>

        <!-- START mobile version -->
        <ProcedureItemMobile v-for="itm in items.loaded"
                             :key="'troubleshooting-results__mobile_'+itm.tree_id"
                             :item="itm"
                             @sessionStart="startSession"
                             @sessionDetails="sessionDetails"
        />
        <!-- END mobile version -->

        <b-notification v-if="notification">
          {{ notification.ck | translate(labels, notification.cp) | replace('[sn]', getCurrentSerial) }}
        </b-notification>

        <b-notification v-else-if="getTroubleshootingKeyword === ''" slot="rows">
          {{ 'emptyQuery' | translate(labels, 'TroubleshootingProcedures') }}
        </b-notification>

        <b-col v-else-if="!items.all.length">
          <div v-if="!loading" class="box">{{ 'noProceduresFound' | translate(labels, 'TroubleshootingProcedures') }}</div>
          <ListEmptyWidget v-else />
        </b-col>
      </b-row>
    </overlay-wrapper>

    <b-error v-else-if="error.present" :code="error.code" data-test-id="troubleshooting-error">
      {{ error.text.ck | translate(labels, error.text.cp) }}
    </b-error>

    <template v-else-if="disabled">
      <ListEmptyWidget />
    </template>

    <b-col v-else class="d-flex align-self-center justify-content-center py-5 box">
      <b-spinner variant="primary" data-test-id="history-loading" />
    </b-col>

    <b-modal v-show="activeItem.tree_id"
             ref="sessionDetailsModal"
             ok-only
             scrollable
             size="lg"
             :title="`${activeItem.title} -${ activeItem.short_description}`"
    >
      <template v-slot:default>
        <b-col cols="12">
          <b-row align-v="center">
            <b-col cols="auto"
                   class="py-3 pr-0 font-condensed"
            >
              {{ 'rank' | translate(labels, 'TroubleshootingProcedures') }}
            </b-col>
            <b-col v-b-tooltip.right="{ title: 'Rank: ' + activeItem.score }" cols="6" sm="5" md="4" lg="3">
              <b-progress height=".5rem"
                          variant="success"
                          :value="activeItem.scoreValue"
              />
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="12"
               class="py-3 width_override"
               v-html="activeItem.long_description"
        />
      </template>

      <template v-slot:modal-footer>
        <div class="d-flex w-100 px-3" :class="(activeItem.sessionError) ? 'justify-content-between' : 'justify-content-end'">
          <div v-if="activeItem.sessionError" class="box bg-gray-400">
            <b-icon icon="lock-alt" class="small align-self-end text-gray-600 mr-2" />
            {{ "sessionStartError" | translate(labels, 'TroubleshootingProcedures') }}
          </div>
          <div class="my-3 text-right">
            <b-btn variant="primary"
                   class="font-condensed text-nowrap"
                   :disabled="session.starting === activeItem.tree_id || activeItem.sessionError"
                   @click.prevent="startSession(activeItem)"
            >
              <template v-if="session.starting === activeItem.tree_id">
                <b-spinner variant="light" small />
              </template>
              {{ 'startSession' | translate(labels, 'TroubleshootingProcedures') }}
            </b-btn>
          </div>
        </div>
      </template>
    </b-modal>

    <LazyVisibleRenderer v-if="rendered"
                         @visible="renderMore"
    />
    <div v-if="rendered" class="text-center mt-3 d-none d-block-ie-only">
      <b-btn variant="primary" class="font-condensed" @click.prevent="renderMore">{{ 'loadMore' | translate(labels, 'CommonFunctional') }}</b-btn>
    </div>
  </b-col>
</template>

<script>
import {mapGetters} from 'vuex'
import ListEmptyWidget from '@/components/common/ListEmptyWidget'
import ErrorSearch from '@/components/common/Machine/ErrorSearch'
import LazyVisibleRenderer from '@/components/common/LazyVisibleRenderer'
import ProcedureItemMobile from '@/components/common/TroubleshootingPro/ProcedureItemMobile'
import BError from '@/components/common/BError'
import BNotification from '@/components/common/BNotification'

import {bdService} from '@/services'

export default {
  conceptName: ['TroubleshootingProcedures', 'CommonErrors', 'CommonFunctional', 'TroubleshootingErrors'],
  components: {
    ErrorSearch,
    ListEmptyWidget,
    ProcedureItemMobile,
    LazyVisibleRenderer,
    BError,
    BNotification
  },
  props: {
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    config: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      loading: false,
      notification: null,
      error: {
        present: false,
        text: null,
        code: null
      },
      showAll: true,
      activeItem: {},
      items: {
        all: [],
        loaded: [],
        step: 40,
        currentIndex: 0
      },
      session: {
        starting: null,
        error: null
      },
      rendered: false
    }
  },
  computed: {
    fields() {
      return [
        {key: 'title', label: this.translatedLabel('procedureName', 'TroubleshootingProcedures')},
        {key: 'short_description', label: this.translatedLabel('description', 'TroubleshootingProcedures')},
        {key: 'view', label: '', sortable: false},
        {key: 'score', label: this.translatedLabel('rank', 'TroubleshootingProcedures'), sortable: false},
        {key: 'tree_id', label: this.translatedLabel('action', 'TroubleshootingProcedures'), sortable: false}
      ]
    },
    ...mapGetters([
      'getCurrentSerial',
      'getCurrentAbbreviation',
      'getTroubleshootingKeyword',
      'getTroubleshootingSearchType'
    ])
  },
  watch: {
    getTroubleshootingKeyword() {
      this.fetchResults(false)
    },
    getTroubleshootingSearchType() {
      this.fetchResults(false)
    },
    getCurrentSerial(s) {
      if (s) this.fetchResults(false)
    }
  },
  created() {
    this.fetchResults(true)
  },
  methods: {
    startSession(itm) {
      this.session.starting = itm.tree_id
      bdService.troubleshootingSessionStart(this.getCurrentSerial, itm.tree_id).then(r => {
        // ToDo: set proper headers for authorization?
        window.open(r.data.url)
        this.session.starting = null
      }).catch(() => {
        this.session.starting = null
        this.$set(itm, 'sessionError', true)
        this.sessionDetails(itm)
      })
    },
    sessionDetails(itm) {
      this.activeItem = itm
      this.$refs['sessionDetailsModal'].show()
    },
    getErrorText(error) {
      if (error.error_code === 403) return {ck: 'unauthorizedError', cp: 'CommonErrors'}
      return {ck: 'searchingError', cp: 'CommonErrors'}
    },
    fetchResults(isInitLoad) {
      if (!this.disabled && this.getCurrentSerial) {
        this.loading = true
        this.error = {
          present: false,
          text: null,
          code: null
        }
        this.items.all = []
        this.items.loaded = []

        if (!this.getTroubleshootingKeyword) {
          this.loading = false
          return
        }

        bdService.fetchTroubleshootingGuides(this.getCurrentSerial, {
          params: {
            symptom: this.getTroubleshootingKeyword,
            isExactMatch: this.getTroubleshootingSearchType
          }
        }).then(r => {
          if (!isInitLoad) this.showAll = false
          if (!isInitLoad || (isInitLoad && this.showAll)) {
            this.items.all = r.data.map(x => {
              x.scoreValue = x.score * 100
              return x
            })
            this.items.loaded = this.items.all.slice(0, this.items.step)
            this.loading = false
            this.rendered = true
          }
        }).catch(e => {
          if (e.error_message && e.error_message.match(/Serial Number [A-Za-z0-9]+ could not be found./g)) {
            const abbreviationErrors = this.labels['TroubleshootingErrors'] || {}
            if (abbreviationErrors['notSupported' + this.getCurrentAbbreviation]) {
              this.notification = {ck: 'notSupported' + this.getCurrentAbbreviation, cp: 'TroubleshootingErrors'}
            } else {
              this.notification = {ck: 'notSupported', cp: 'TroubleshootingErrors'}
            }
            this.loading = false
          }
          else if (!e.isCancelled) {
            this.error = {
              present: true,
              text: this.getErrorText(e),
              code: e.error_code || null
            }
            this.loading = false
          }
        })
      }
    },
    renderMore(isVisible) {
      if (isVisible && this.items.loaded.length < this.items.all.length) {
        this.rendered = false
        this.items.currentIndex = this.items.loaded.length
        this.items.loaded.push(...(this.items.all.slice(this.items.currentIndex, this.items.currentIndex + this.items.step)))
        this.$nextTick(() => {
          this.rendered = true
        })
      }
    }
  }
}
</script>

<style lang="scss">
.width_override {
  table, td {
    width: auto !important;
  }
}

.troubleshooting_table {
  td[aria-colindex="1"] {
    max-width: 150px !important;
    width: 150px;
  }

  td[aria-colindex="4"] {
    text-align: center;
    width: 135px !important;
  }

  tr {
    th:first-child {
      padding-left: .6125rem;
    }
  }
}
</style>
